import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaStore, FaTag, FaTags, FaPercent, FaCalendarAlt, 
  FaTrash, FaFilter, FaSearch, FaSortAmountDown, 
  FaUpload, FaStar, FaStarHalfAlt, FaPlus, FaExclamationCircle, FaChartLine, FaHandshake
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './MarketplaceListing.css';

const MarketplaceListing = () => {
  const { t } = useTranslation();
  const [marketplaceProducts, setMarketplaceProducts] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productQuantities, setProductQuantities] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortOption, setSortOption] = useState('published_at-desc');
  const [categories, setCategories] = useState([]);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [commissionRate, setCommissionRate] = useState(10);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isFeatured, setIsFeatured] = useState(false);
  const [availableUntil, setAvailableUntil] = useState('');
  const [marketplaceQuantities, setMarketplaceQuantities] = useState({});
  const navigate = useNavigate();

  // Add dummy data for experience users
  const dummyMarketplaceProducts = [
    {
      _id: 'dummy_market_1',
      product_id: 'exp1001',
      title: 'Eco-Friendly Water Bottle',
      images: [{ url: '/waterbottle.png' }],
      status: 'active',
      is_featured: true,
      price_range: { min: 19.99, max: 19.99, currency: 'EUR' },
      commission_rate: 15,
      available_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      categories: ['Sustainable', 'Drinkware'],
      description: 'A stylish and reusable water bottle made from recycled materials.',
      published_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      _id: 'dummy_market_2',
      product_id: 'exp1002',
      title: 'Handmade Ceramic Mug Set',
      images: [{ url: '/astromug.png' }],
      status: 'active',
      is_featured: false,
      price_range: { min: 35.00, max: 45.00, currency: 'EUR' },
      commission_rate: 20,
      available_until: null,
      categories: ['Home Goods', 'Kitchenware', 'Handmade'],
      description: 'Set of two unique, handcrafted ceramic mugs.',
      published_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString()
    }
  ];

  // Updated dummyAvailableProducts to include all products from ProductsList.js's dummy data
  // with a 'price' attribute added to variants.
  const dummyAvailableProducts = [
    { id: 'exp001', title: 'Experience T-Shirt - Galaxy', images: [{ src: '/tshirtgalaxy.png' }], status: 'active', variants: [{ inventory_quantity: 15, title: 'M / Black', price: '24.99' }, { inventory_quantity: 5, title: 'L / Black', price: '24.99' }], product_type: 'Apparel', vendor: 'Experience Wear', tags: ['cotton', 'new', 'space'] },
    { id: 'exp002', title: 'Experience Mug - Astronaut', images: [{ src: '/astromug.png' }], status: 'active', variants: [{ inventory_quantity: 30, title: 'Standard', price: '14.99' }], product_type: 'Homeware', vendor: 'Experience Goods', tags: ['ceramic', 'gift'] },
    { id: 'exp003', title: 'Experience Hoodie - Nebula', images: [{ src: '/hoodie.png' }], status: 'draft', variants: [{ inventory_quantity: 8, title: 'S / Blue', price: '39.99' }, { inventory_quantity: 12, title: 'M / Blue', price: '39.99' }], product_type: 'Apparel', vendor: 'Experience Wear', tags: ['warm', 'winter'] },
    { id: 'exp004', title: 'Experience Cap - Rocket', images: [{ src: '/rocketcap.png' }], status: 'archived', variants: [{ inventory_quantity: 0, title: 'One Size', price: '19.99' }], product_type: 'Accessory', vendor: 'Experience Gear', tags: ['summer', 'promo'] },
    { id: 'exp005', title: 'Experience Water Bottle - Stainless Steel', images: [{ src: '/waterbottle.png' }], status: 'active', variants: [{ inventory_quantity: 50, title: '750ml', price: '22.50' }], product_type: 'Drinkware', vendor: 'Experience Goods', tags: ['eco', 'reusable'] },
    { id: 'exp006', title: 'Experience Socks - Alien Pattern', images: [{ src: '/alienpattern.png' }], status: 'active', variants: [{ inventory_quantity: 22, title: 'One Size', price: '9.99' }], product_type: 'Apparel', vendor: 'Experience Wear', tags: ['fun', 'novelty'] },
    { id: 'exp007', title: 'Experience Backpack - Voyager', images: [{ src: '/voyagerbag.png' }], status: 'active', variants: [{ inventory_quantity: 18, title: 'Standard', price: '49.99' }], product_type: 'Accessory', vendor: 'Experience Gear', tags: ['travel', 'durable'] },
    { id: 'exp008', title: 'Experience Poster - Solar System', images: [{ src: '/poster.png' }], status: 'draft', variants: [{ inventory_quantity: 40, title: 'A3', price: '12.00' }], product_type: 'Decor', vendor: 'Experience Prints', tags: ['education', 'wall art'] },
    { id: 'exp009', title: 'Experience Keychain - Spaceship', images: [{ src: '/keychain.png' }], status: 'active', variants: [{ inventory_quantity: 100, title: 'Standard', price: '7.50' }], product_type: 'Accessory', vendor: 'Experience Goods', tags: ['collectible'] },
    { id: 'exp010', title: 'Experience Phone Case - Mars Rover', images: [{ src: '/marsrover.png' }], status: 'active', variants: [{ inventory_quantity: 3, title: 'iPhone X', price: '18.99' }, { inventory_quantity: 7, title: 'iPhone 12', price: '18.99' }], product_type: 'Accessory', vendor: 'Experience Tech', tags: ['protective', 'mars'] },
    { id: 'exp011', title: 'Experience Beanie - Comet Trail', images: [], status: 'archived', variants: [{ inventory_quantity: 0, title: 'One Size', price: '15.00' }], product_type: 'Apparel', vendor: 'Experience Wear', tags: ['winter', 'limited'] },
    { id: 'exp012', title: 'Experience Stickers Pack - Cosmic', images: [{ src: '/stickers.png' }], status: 'active', variants: [{ inventory_quantity: 200, title: 'Pack of 50', price: '6.99' }], product_type: 'Stationery', vendor: 'Experience Prints', tags: ['decor', 'laptop'] },
    { id: 'exp013', title: 'Experience Scarf - Starry Night', images: [{ src: '/scarf.png' }], status: 'active', variants: [{ inventory_quantity: 12, title: 'Standard', price: '28.00' }], product_type: 'Apparel', vendor: 'Experience Wear', tags: ['fashion', 'soft'] },
    { id: 'exp014', title: 'Experience Notebook - Constellation', images: [{ src: '/notebook.png' }], status: 'draft', variants: [{ inventory_quantity: 60, title: 'A5 Lined', price: '10.00' }], product_type: 'Stationery', vendor: 'Experience Prints', tags: ['writing', 'journal'] },
    { id: 'exp015', title: 'Experience Desk Mat - Deep Space', images: [{ src: '/deskmat.png' }], status: 'active', variants: [{ inventory_quantity: 25, title: 'Large', price: '32.00' }], product_type: 'Office', vendor: 'Experience Goods', tags: ['gaming', 'ergonomic'] },
    { id: 'exp016', title: 'Experience Puzzle - Earth View', images: [{ src: '/puzzle.png' }], status: 'active', variants: [{ inventory_quantity: 10, title: '1000 pieces', price: '29.00' }], product_type: 'Toy', vendor: 'Experience Games', tags: ['family', 'challenge'] },
  ];

  const dummyCategories = ['Sustainable', 'Drinkware', 'Home Goods', 'Kitchenware', 'Handmade', 'Apparel', 'Organic', 'Accessory', 'Homeware', 'Decor', 'Stationery', 'Office', 'Toy', 'Tech'];

  // Add dummy marketplaces data
  const dummyMarketplaces = [
    {
      id: 'amazon_de',
      name: 'Amazon DE',
      icon: 'https://via.placeholder.com/24x24.png?text=A',
      commission_rate: 15,
      min_quantity: 10,
      max_quantity: 1000
    },
    {
      id: 'etsy',
      name: 'Etsy Handmade',
      icon: 'https://via.placeholder.com/24x24.png?text=E',
      commission_rate: 20,
      min_quantity: 1,
      max_quantity: 100
    },
    {
      id: 'tiktok_shop',
      name: 'TikTok Shop',
      icon: 'https://via.placeholder.com/24x24.png?text=T',
      commission_rate: 25,
      min_quantity: 5,
      max_quantity: 500
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = sessionStorage.getItem('access_token');
        const userDataString = sessionStorage.getItem('userData');
        let userRole = null;

        if (!token) {
          navigate('/');
          setIsLoading(false);
          return;
        }

        if (userDataString) {
          try {
            const userData = JSON.parse(userDataString);
            userRole = userData.role;
          } catch (parseError) {
            console.error("Failed to parse user data:", parseError);
          }
        }

        if (userRole === 'experience') {
          // Use dummy data for experience users
          setMarketplaceProducts(dummyMarketplaceProducts);
          setProductsList(dummyAvailableProducts);
          setCategories(dummyCategories);
          setIsLoading(false);
          return;
        }

        // Existing logic for non-experience users
        const marketplaceResponse = await axios.get(
          '/api/shopify/my/marketplace/products',
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        const productsResponse = await axios.get(
          '/api/shopify/get_products',
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        const allCategories = marketplaceResponse.data.products
          .flatMap(product => product.categories || [])
          .filter((category, index, self) => self.indexOf(category) === index);
        
        setMarketplaceProducts(marketplaceResponse.data.products);
        setProductsList(productsResponse.data.products);
        setCategories(allCategories);
      } catch (err) {
        setError(err.message);
        if (err.response && (err.response.status === 401 || err.response.status === 422)) {
          sessionStorage.removeItem('access_token');
          navigate('/');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleCheckboxChange = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });

    // Initialize quantity to 1 when adding a product
    if (!productQuantities[productId]) {
      setProductQuantities(prev => ({
        ...prev,
        [productId]: 1
      }));
    }
  };

  const handleQuantityChange = (productId, quantity) => {
    setProductQuantities(prev => ({
      ...prev,
      [productId]: Math.max(1, parseInt(quantity) || 1)
    }));
  };

  const handlePublishClick = () => {
    setShowPublishModal(true);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  // Add new handler for marketplace quantity changes
  const handleMarketplaceQuantityChange = (productId, marketplaceId, quantity) => {
    setMarketplaceQuantities(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [marketplaceId]: Math.max(1, parseInt(quantity) || 1)
      }
    }));
  };

  const handlePublishSubmit = async () => {
    try {
      setIsPublishing(true);
      
      const token = sessionStorage.getItem('access_token');
      const userDataString = sessionStorage.getItem('userData');
      let userRole = null;

      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          userRole = userData.role;
        } catch (parseError) {
          console.error("Failed to parse user data:", parseError);
        }
      }

      if (userRole === 'experience') {
        // Create new products for each marketplace
        const newProducts = selectedProducts.flatMap(productId => {
          const product = productsList.find(p => p.id === productId);
          if (!product) return [];
          const productMarketplaces = marketplaceQuantities[productId] || {};
          
          return Object.entries(productMarketplaces).map(([marketplaceId, quantity]) => {
            const marketplace = dummyMarketplaces.find(m => m.id === marketplaceId);
            if (!marketplace) return null;

            const price = product.variants && product.variants.length > 0 && product.variants[0].price
              ? parseFloat(product.variants[0].price)
              : 0;

            return {
              _id: `dummy_market_${Date.now()}_${productId}_${marketplaceId}`,
              product_id: productId,
              title: product.title,
              images: product.images,
              status: 'active',
              is_featured: isFeatured,
              price_range: { 
                min: price, 
                max: price, 
                currency: 'EUR' 
              },
              commission_rate: marketplace.commission_rate,
              available_until: availableUntil ? new Date(availableUntil).toISOString() : null,
              categories: selectedCategories,
              published_at: new Date().toISOString(),
              marketplace_id: marketplaceId,
              marketplace_name: marketplace.name,
              quantity: quantity
            };
          }).filter(p => p !== null);
        });

        setMarketplaceProducts(prev => [...prev, ...newProducts]);
        setSelectedProducts([]);
        setProductQuantities({});
        setMarketplaceQuantities({});
        setShowPublishModal(false);
        alert(t('marketplace.publishSuccess'));
        setIsPublishing(false);
        return;
      }

      // Existing logic for non-experience users
      const productsWithQuantities = selectedProducts.map(productId => ({
        product_id: productId,
        quantity: productQuantities[productId] || 1
      }));
      
      await axios.post(
        '/api/shopify/publish_to_marketplace',
        {
          products: productsWithQuantities,
          commission_rate: commissionRate,
          categories: selectedCategories,
          is_featured: isFeatured,
          available_until: availableUntil ? new Date(availableUntil).toISOString() : null
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      const response = await axios.get(
        '/api/shopify/marketplace/products',
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setMarketplaceProducts(response.data.products);
      setSelectedProducts([]);
      setProductQuantities({});
      setShowPublishModal(false);
      alert(t('marketplace.publishSuccess'));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleRemoveFromMarketplace = async (productId) => {
    try {
      const token = sessionStorage.getItem('access_token');
      const userDataString = sessionStorage.getItem('userData');
      let userRole = null;

      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          userRole = userData.role;
        } catch (parseError) {
          console.error("Failed to parse user data:", parseError);
        }
      }

      if (userRole === 'experience') {
        // Simulate removal for experience users
        setMarketplaceProducts(prevProducts => 
          prevProducts.filter(product => product.product_id !== productId)
        );
        alert(t('marketplace.removeSuccess'));
        return;
      }

      // Existing logic for non-experience users
      await axios.delete(
        '/api/shopify/remove_from_marketplace',
        {
          headers: { Authorization: `Bearer ${token}` },
          data: {
            product_ids: [productId]
          }
        }
      );
      
      setMarketplaceProducts(marketplaceProducts.filter(product => 
        product.product_id !== productId
      ));
      
      alert(t('marketplace.removeSuccess'));
    } catch (err) {
      setError(err.message);
      if (err.response && (err.response.status === 401 || err.response.status === 422)) {
        sessionStorage.removeItem('access_token');
        navigate('/');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('marketplace.noExpiration');
    return new Date(dateString).toLocaleDateString();
  };

  // Filter marketplace products
  const filterProducts = () => {
    let filtered = marketplaceProducts;
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(product => 
        product.title.toLowerCase().includes(term) ||
        (product.description && product.description.toLowerCase().includes(term))
      );
    }
    
    // Apply category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(product => 
        product.categories && product.categories.includes(filterCategory)
      );
    }
    
    // Apply sort
    const [field, direction] = sortOption.split('-');
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (field) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'price':
          comparison = a.price_range.min - b.price_range.min;
          break;
        case 'commission':
          comparison = a.commission_rate - b.commission_rate;
          break;
        case 'published_at':
          comparison = new Date(a.published_at) - new Date(b.published_at);
          break;
        default:
          comparison = 0;
      }
      
      return direction === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  };

  // Get products not yet in marketplace
  const getAvailableProducts = () => {
    const marketplaceProductIds = marketplaceProducts.map(p => p.product_id);
    return productsList.filter(product => !marketplaceProductIds.includes(product.id));
  };

  const filteredProducts = filterProducts();
  const availableProducts = getAvailableProducts();

  // Modify the publish modal JSX
  const renderPublishModal = () => {
    const userDataString = sessionStorage.getItem('userData');
    let userRole = null;
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        userRole = userData.role;
      } catch (parseError) {
        console.error("Failed to parse user data:", parseError);
      }
    }

    return (
      <div className="una-modal-overlay">
        <div className="una-modal">
          <h3>{t('marketplace.publishToMarketplace')}</h3>
          
          <div className="una-modal-content">
            <div className="una-form-group">
              <label>{t('marketplace.commissionRate')}</label>
              <div className="una-range-input-group">
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={commissionRate}
                  onChange={(e) => setCommissionRate(parseInt(e.target.value))}
                />
                <span className="una-range-value">{commissionRate}%</span>
              </div>
              <p className="una-helper-text">{t('marketplace.commissionHelp')}</p>
            </div>
            
            <div className="una-form-group">
              <label>{t('marketplace.selectCategories')}</label>
              <div className="una-categories-selection">
                {categories.map(category => (
                  <label key={category} className="una-category-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category)}
                      onChange={() => handleCategoryChange(category)}
                    />
                    {category}
                  </label>
                ))}
              </div>
              <button 
                className="una-add-category-button"
                onClick={() => {
                  const newCategory = prompt(t('marketplace.newCategoryPrompt'));
                  if (newCategory && !categories.includes(newCategory)) {
                    setCategories([...categories, newCategory]);
                    setSelectedCategories([...selectedCategories, newCategory]);
                  }
                }}
              >
                <FaPlus /> {t('marketplace.addNewCategory')}
              </button>
            </div>
            
            <div className="una-form-group">
              <label className="una-checkbox-label">
                <input
                  type="checkbox"
                  checked={isFeatured}
                  onChange={(e) => setIsFeatured(e.target.checked)}
                />
                {t('marketplace.markAsFeatured')}
              </label>
              <p className="una-helper-text">{t('marketplace.featuredHelp')}</p>
            </div>
            
            <div className="una-form-group">
              <label>
                <FaCalendarAlt /> {t('marketplace.availableUntil')}
              </label>
              <input
                type="date"
                value={availableUntil}
                onChange={(e) => setAvailableUntil(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="una-helper-text">{t('marketplace.availableUntilHelp')}</p>
            </div>

            {/* New section for marketplace selection - only for experience users */}
            {userRole === 'experience' && (
              <div className="una-form-group">
                <label>{t('marketplace.selectMarketplaces')}</label>
                <div className="una-marketplaces-selection">
                  {selectedProducts.map(productId => {
                    const product = productsList.find(p => p.id === productId);
                    return (
                      <div key={productId} className="una-product-marketplaces">
                        <h4>{product.title}</h4>
                        <div className="una-marketplace-options">
                          {dummyMarketplaces.map(marketplace => (
                            <div key={marketplace.id} className="una-marketplace-option">
                              <div className="una-marketplace-info">
                                <img 
                                  src={marketplace.icon} 
                                  alt={marketplace.name} 
                                  className="una-marketplace-icon"
                                />
                                <div className="una-marketplace-details">
                                  <span className="una-marketplace-name">{marketplace.name}</span>
                                  <span className="una-marketplace-commission">
                                    {t('marketplace.commission')}: {marketplace.commission_rate}%
                                  </span>
                                </div>
                              </div>
                              <div className="una-marketplace-quantity">
                                <label>{t('marketplace.quantity')}:</label>
                                <input
                                  type="number"
                                  min={marketplace.min_quantity}
                                  max={marketplace.max_quantity}
                                  value={marketplaceQuantities[productId]?.[marketplace.id] || marketplace.min_quantity}
                                  onChange={(e) => handleMarketplaceQuantityChange(
                                    productId,
                                    marketplace.id,
                                    e.target.value
                                  )}
                                  className="una-quantity-input"
                                />
                                <span className="una-quantity-limits">
                                  ({marketplace.min_quantity}-{marketplace.max_quantity})
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          <div className="una-modal-footer">
            <button 
              className="una-cancel-button"
              onClick={() => setShowPublishModal(false)}
            >
              {t('common.cancel')}
            </button>
            <button 
              className="una-submit-button"
              onClick={handlePublishSubmit}
              disabled={isPublishing}
            >
              {isPublishing ? t('common.publishing') : t('marketplace.publishNow')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (error) return (
    <div className="una-marketplace-container">
      <div className="una-marketplace-error">
        <FaExclamationCircle />
        <h2>{t('common.error')}</h2>
        <p>{error}</p>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="una-marketplace-container">
        <div className="una-marketplace-loading-screen">
        <div className="una-loading-content">
          <div className="una-loading-logo">
            <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
          </div>
            <h2>{t('marketplace.loadingTitle')}</h2>
            <div className="una-loading-progress">
              <div className="una-progress-bar">
                <div className="una-progress-glow"></div>
              </div>
            </div>
            <div className="una-loading-metrics">
              <div className="una-metric-item">
                <FaStore />
                <span>{t('marketplace.fetchingProducts')}</span>
              </div>
              <div className="una-metric-item">
                <FaChartLine />
                <span>{t('marketplace.loadingStats')}</span>
              </div>
              <div className="una-metric-item">
                <FaHandshake />
                <span>{t('marketplace.processingData')}</span>
              </div>
              <div className="una-metric-item">
                <FaUpload />
                <span>{t('marketplace.preparingMarketplace')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="una-marketplace-container">
      <div className="una-marketplace-content">
        <div className="una-marketplace-filters">
          <div className="una-search-group">
            <FaSearch className="una-search-icon" />
            <input
              type="text"
              className="una-search-input"
              placeholder={t('marketplace.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="una-filter-group">
            <label className="una-filter-label">
              <FaFilter /> {t('marketplace.categoryFilter')}
            </label>
            <select 
              className="una-filter-select"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="all">{t('marketplace.allCategories')}</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          
          <div className="una-filter-group">
            <label className="una-filter-label">
              <FaSortAmountDown /> {t('marketplace.sort')}
            </label>
            <select 
              className="una-filter-select"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="published_at-desc">{t('marketplace.sortNewest')}</option>
              <option value="published_at-asc">{t('marketplace.sortOldest')}</option>
              <option value="title-asc">{t('marketplace.sortTitleAZ')}</option>
              <option value="title-desc">{t('marketplace.sortTitleZA')}</option>
              <option value="price-asc">{t('marketplace.sortPriceLow')}</option>
              <option value="price-high">{t('marketplace.sortPriceHigh')}</option>
              <option value="commission-desc">{t('marketplace.sortCommissionHigh')}</option>
            </select>
          </div>
        </div>

        <div className="una-marketplace-sections">
          <div className="una-section">
            <div className="una-section-header">
              <h3>{t('marketplace.availableProducts')} ({availableProducts.length})</h3>
              <button 
                onClick={handlePublishClick}
                className="una-publish-button"
                disabled={selectedProducts.length === 0}
              >
                <FaUpload /> {t('marketplace.publishSelected')} 
                {selectedProducts.length > 0 && ` (${selectedProducts.length})`}
              </button>
            </div>
            
            {availableProducts.length === 0 ? (
              <div className="una-empty-state">
                <FaStore />
                <p>{t('marketplace.noAvailableProducts')}</p>
              </div>
            ) : (
              <div className="una-products-selection-grid">
                {availableProducts.map((product) => (
                  <div key={product.id} className="una-product-selection-card">
                    <div className="una-product-checkbox-wrapper">
                      <input
                        type="checkbox"
                        className="una-product-checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => handleCheckboxChange(product.id)}
                        aria-label={`Select ${product.title}`}
                      />
                    </div>
                    
                    <div className="una-product-content">
                      <div className="una-product-image-wrapper">
                        {product.images && product.images.length > 0 ? (
                          <img 
                            src={product.images[0].src} 
                            alt={product.title}
                            className="una-product-thumbnail"
                          />
                        ) : (
                          <div className="una-product-no-image">
                            <FaStore />
                          </div>
                        )}
                      </div>
                      
                      <div className="una-product-info">
                        <h4>{product.title}</h4>
                        
                        {product.variants && product.variants.length > 0 && (
                          <p className="una-product-price">
                            {product.variants[0].price} €
                          </p>
                        )}
                        
                        {product.status && (
                          <span className={`una-product-status ${product.status.toLowerCase()}`}>
                            {product.status}
                          </span>
                        )}
                        
                        {selectedProducts.includes(product.id) && (
                          <div className="una-product-quantity">
                            <label>{t('marketplace.quantity')}:</label>
                            <input
                              type="number"
                              min="1"
                              value={productQuantities[product.id] || 1}
                              onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                              className="una-quantity-input"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="una-section">
            <h3>{t('marketplace.publishedProducts')} ({marketplaceProducts.length})</h3>
            
            {filteredProducts.length === 0 ? (
              <div className="una-empty-state">
                <FaStore />
                <p>{t('marketplace.noPublishedProducts')}</p>
              </div>
            ) : (
              <div className="una-marketplace-grid">
                {filteredProducts.map((product) => (
                  <div key={product._id} className="una-marketplace-card">
                    <div className="una-product-image-container">
                      {product.images && product.images.length > 0 ? (
                        <img 
                          src={product.images[0].url} 
                          alt={product.title} 
                          className="una-product-image" 
                        />
                      ) : (
                        <div className="una-product-no-image">
                          <FaStore />
                        </div>
                      )}
                      
                      {product.is_featured && (
                        <div className="una-featured-badge">
                          <FaStar /> {t('marketplace.featured')}
                        </div>
                      )}
                    </div>
                    
                    <div className="una-product-details">
                      <h4>{product.title}</h4>
                      
                      <div className="una-product-meta">
                        <p className="una-product-price">
                          {product.price_range.min} {product.price_range.currency}
                          {product.price_range.min !== product.price_range.max && 
                            ` - ${product.price_range.max} ${product.price_range.currency}`}
                        </p>
                        
                        <p className="una-product-commission">
                          <FaPercent /> {t('marketplace.commission')}: {product.commission_rate}%
                        </p>
                        
                        {product.available_until && (
                          <p className="una-product-expiry">
                            <FaCalendarAlt /> {t('marketplace.availableUntil')}: {formatDate(product.available_until)}
                          </p>
                        )}
                      </div>
                      
                      <div className="una-product-categories">
                        {product.categories && product.categories.map(category => (
                          <span key={category} className="una-category-tag">
                            <FaTags /> {category}
                          </span>
                        ))}
                      </div>
                      
                      <button
                        onClick={() => handleRemoveFromMarketplace(product.product_id)}
                        className="una-remove-button"
                      >
                        <FaTrash /> {t('marketplace.removeFromMarketplace')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {showPublishModal && renderPublishModal()}
      </div>
    </div>
  );
};

export default MarketplaceListing;